import { createSelector } from 'reselect'
import { State } from 'interfaces/state'
import { CreditAccountCollection, CreditExtendedFormValues, CreditFormValues, CreditTotals } from 'interfaces/credit'
import { TimeframeTyped } from 'interfaces/global'
import { createOwnPropsSelector, memberInfoSelector } from 'selectors/globalSelectors'
import { getTimeframeLastMonth } from 'helpers/dateTimeHelpers'
import { createEmptyCollection } from 'helpers/normalizeHelpers'
import { formValueSelector, getFormValues } from 'redux-form'
import { LocationOption } from 'interfaces/locations'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import { getCreditFormInitialValues } from 'helpers/creditHelpers'
import { FORM_SETTINGS_CREDIT, FORM_SETTINGS_CREDIT_EXTENDED } from 'constants/forms'

export const highlightedGenericCategorySelector = (state: State) =>
  state.app.creditscore.highlightedCategoryId

// ===========================================================================//
//                                 ACCOUNTS                                   //
// ===========================================================================//

export const creditAccountsStateSelector = ({ data }: State) => data.creditscore.accounts

const ZERO_CREDIT_ACCOUNTS: CreditAccountCollection = createEmptyCollection()
export const creditAccountsSelector = createSelector(
  memberInfoSelector,
  creditAccountsStateSelector,
  createOwnPropsSelector<TimeframeTyped>(),
  (member, accounts, { timeframe }): CreditAccountCollection | null => {
    if (!member) {
      return null
    }

    if (!member.is_pii_verified) {
      return ZERO_CREDIT_ACCOUNTS
    }

    const month = getTimeframeLastMonth(timeframe)
    return accounts[month]
  },
)
export const areCreditAccountsLoadedSelector = createSelector(
  creditAccountsSelector,
  Boolean,
)


// ===========================================================================//
//                                   TOTALS                                   //
// ===========================================================================//

export const creditTotalsStateSelector = ({ data }: State) => data.creditscore.totals

const ZERO_CREDIT_TOTALS: CreditTotals = {
  credit_age: 0,
  available_credit: 0,
  inquiries_count: 0,
  open_accounts: 0,
  total_balance: 0,
  utilization: 0,
}
export const creditTotalsSelector = createSelector(
  memberInfoSelector,
  creditTotalsStateSelector,
  createOwnPropsSelector<TimeframeTyped>(),
  (member, totals, { timeframe }): CreditTotals | null => {
    if (!member) {
      return null
    }

    return member.is_pii_verified
      ? totals[getTimeframeLastMonth(timeframe)]
      : ZERO_CREDIT_TOTALS
  },
)
export const areCreditTotalsLoadedSelector = createSelector(
  creditTotalsSelector,
  Boolean,
)


// ===========================================================================//
//                                   LINKAGE                                  //
// ===========================================================================//

const creditLinkageSelector = (state: State) =>
  state.app.creditscore.linkage

export const creditLinkagePhaseSelector = createSelector(
  creditLinkageSelector,
  (credit) => credit.phase,
)

export const isPIIVerifiedSelector = createSelector(
  memberInfoSelector,
  (member): boolean | null =>
    member ? member.is_pii_verified : null,
)

export const isCreditFileLinkedSelector = createSelector(
  memberInfoSelector,
  (member): boolean | null =>
    member ? member.is_creditfile_linked : null,
)

// ===========================================================================//
//                                 CREDIT FORM                                //
// ===========================================================================//

export const creditFormNameSelector = createSelector(
  creditLinkagePhaseSelector,
  (phase) => {

    const formNames = [FORM_SETTINGS_CREDIT, FORM_SETTINGS_CREDIT_EXTENDED]
    return formNames[phase === 'form-extended' ? 1 : 0]
  },
)

export const creditFormValuesSelector = (state: State): CreditFormValues | CreditExtendedFormValues | null => {
  const formName = creditFormNameSelector(state)
  return getFormValues<CreditFormValues | CreditExtendedFormValues>(formName)(state)
}

export const creditFormInitialValuesSelector = createSelector(
  creditLinkagePhaseSelector,
  memberInfoSelector,
  creditFormValuesSelector,
  (phase, member, creditFormValues): CreditFormValues | CreditExtendedFormValues => {
    if (!member) {
      throw createForgottenGuardError('CreditFormContainer', 'memberInfoGuard')
    }
    return getCreditFormInitialValues(member, phase, creditFormValues)
  },
)

export const creditFormCityValueSelector = (state: State): LocationOption | null => {
  const formName = creditFormNameSelector(state)
  return formValueSelector(formName)(state, 'city')
}
